import { format, isSameDay } from 'date-fns';
import { useEffect } from 'react';
import { useGanttInstanceId } from '../Gantt/utils';
import { DateRange, GanttFilter, useGanttFilterStore } from './useGanttFilterStore';
import { AVAILABLE_QUERY_PARAMS, useProductionPlanQueryParams } from './useProductionPlanQueryParams';

const areGanttFiltersEqual = (left: GanttFilter[], right: GanttFilter[]) =>
  left.length === right.length && left.every((filter) => right.includes(filter));

const areGanttDateRangesEqual = (left: DateRange, right: DateRange) =>
  isSameDay(left.from, right.from) && isSameDay(left.to, right.to);

const serializeGanttFilters = (filters: GanttFilter[]) => [...filters].sort().join(',');

export const useSyncGanttFilterStoreWithQueryParams = () => {
  const instanceId = useGanttInstanceId();
  const dateRange = useGanttFilterStore((state) => state.ganttDateRange);
  const filters = useGanttFilterStore((state) => state.ganttFilters);
  const setGanttDateRange = useGanttFilterStore((state) => state.setGanttDateRange);
  const setGanttFilters = useGanttFilterStore((state) => state.setGanttFilters);
  const { queryParams, setQueryParams } = useProductionPlanQueryParams();

  useEffect(() => {
    const { ganttFrom, ganttTo, ganttFilters = [] } = queryParams;
    const currentState = useGanttFilterStore.getState();

    if (
      ganttFrom &&
      ganttTo &&
      !areGanttDateRangesEqual(currentState.ganttDateRange, { from: ganttFrom, to: ganttTo })
    ) {
      setGanttDateRange(instanceId, { from: ganttFrom, to: ganttTo });
    }

    if (ganttFilters.length > 0 && !areGanttFiltersEqual(ganttFilters, currentState.ganttFilters)) {
      setGanttFilters(instanceId, ganttFilters);
    }
    // Restore only on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Read latest store state so hydrate in the previous effect is reflected before the first write
    const { ganttDateRange, ganttFilters } = useGanttFilterStore.getState();

    setQueryParams([
      {
        key: AVAILABLE_QUERY_PARAMS.ganttFrom,
        value: format(ganttDateRange.from, 'yyyy-MM-dd'),
      },
      {
        key: AVAILABLE_QUERY_PARAMS.ganttTo,
        value: format(ganttDateRange.to, 'yyyy-MM-dd'),
      },
      {
        key: AVAILABLE_QUERY_PARAMS.ganttFilters,
        value: ganttFilters.length > 0 ? serializeGanttFilters(ganttFilters) : null,
      },
    ]);
  }, [dateRange.from, dateRange.to, filters, setQueryParams]);
};
